import React, { useState, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { pathToRegexp } from 'path-to-regexp'
import throttle from 'lodash/throttle'

import { Grid, TextField, Typography, Popper } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Autocomplete from '@material-ui/lab/Autocomplete'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

import { useLiterals } from '~/components/Literals'
import useViewportSize from '~/components/useViewportSize'
import DrawerProductByStore from './DrawerProductByStore'

import api from '~/api'
import track from '~/track'

// styles (mui)
const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: 'relative',
    background: theme.palette.background.default
  },
  wrapperAutocomplete: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    cursor: 'pointer',
    zIndex: 2
  },
  endAdornment: {
    top: 'calc(50% - 20px)'
  },
  paper: {
    maxHeight: 300,
    overflowY: 'auto',
    background: theme.palette.background.default,
    ...theme.gofind.scrollBar
  },
  listbox: {
    maxHeight: '100%'
  },
  groupLabel: {
    fontSize: 16,
    color: '#000'
  },
  inputRoot: {
    '&.Mui-disabled': { border: '1px solid #C4C4C4' },
    '& button:disabled svg': { fill: '#C4C4C4' }
  },
  input: {
    userSelect: 'none',
    '&::placeholder': {
      color: theme.palette.text.primary,
      opacity: 1
    },
    '&::selection': {
      color: 'none'
    }
  },
  noOptions: {
    color: theme.palette.text.primary
  }
}))

function getSlug(value) {
  return value
    ?.toLowerCase()
    ?.replace(/\s+/g, '-')
    ?.replace(/[^a-z0-9-]/g, '')
}

function trackApi(data, product, store, config, position) {
  api.clickTrack({
    gtin: product && product.gtin,
    productName: product && product.name,
    name: store ? store.name : 'all',
    cnpj: store ? store.cnpj : '00000000000000',
    brand: config.isOndetem ? product.brand : null,
    user: position?.user,
    ...data
  })
}

function CustomPopper(props) {
  return <Popper {...props} container={document.getElementById('gofind-finder') || document.body} />
}

export default function SearchProductByStore({
  product,
  isProductDetail = false,
  isEcommerce = false,
  position: positionHelper,
  config,
  setOpenEcommerce,
  setApiEcommerce
}) {
  const mounted = useRef(false)
  const classes = useStyles()
  const history = useHistory()
  const viewport = useViewportSize()
  const { getLiteral } = useLiterals()

  const { brandName } = config

  const [inputValue, setInputValue] = useState('')
  const [selected, setSelected] = useState(null)
  const [open, setOpen] = useState(false)
  const [openDrawer, setOpenDrawer] = useState(false)
  const [options, setOptions] = useState([])
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)

  const defaultProduct = {
    gtin: 'all',
    name: `${getLiteral('1054')} ${getLiteral('1996').toLowerCase()}`,
    imageurl: ''
  }

  const isMobile = viewport.width < 930
  const urlRegex = pathToRegexp('/loja/:cnpj', null, { sensitive: true })
  const isUrlStoreEncrypt = urlRegex.test(location.pathname)

  useEffect(() => {
    mounted.current = true
    return () => (mounted.current = false)
  }, [])

  const promiseOptions = async (inputValue) => {
    setLoading(true)

    const params = positionHelper.getParams?.()

    try {
      const response = await api.search({ ...params, name: inputValue || '' })
      setProducts(response)

      return response
    } catch (err) {
      setProducts([])

      return []
    }
  }

  const handleSearch = throttle((q) => {
    promiseOptions(q).then((result) => {
      if (mounted.current) {
        setLoading(false)
        setOptions(result)
      }

      return
    })
  }, 1500)

  useEffect(() => {
    handleSearch(inputValue)
  }, [inputValue])

  const handleSelect = async (_, item, reason) => {
    if (!item || (selected && item.gtin === selected.gtin)) return

    if (reason === 'blur') return

    if (reason === 'clear') {
      setInputValue('')
      setOpen(true)

      return
    }

    if (!item || item.gtin === 'all') {
      setSelected(null)
      mounted.current && history.push('/produto/todos')
      return
    }

    if (!mounted.current) return

    if (isEcommerce) {
      const store = null
      const product = item

      setApiEcommerce({ name: 'ecommerceGoogle', location: positionHelper?.user?.city })
      trackApi({ event: 'ecommerce', brandName }, product, store, config, positionHelper)

      item && setOpenEcommerce(item)
    }

    setSelected(item)

    isProductDetail
      ? history.replace(`/produto/${item.gtin}/detalhes`, {
          fromApp: true,
          prevPath: history.location.pathname
        })
      : history.push(`/produto/${getSlug(item?.name)}/${item?.gtin}`, {
          from: `${history.location.pathname}${history.location.search}`
        })

    track('selecionarProduto', { category: 'menu', name: item?.name })
  }

  useEffect(() => {
    if (product && mounted.current) setSelected(product)
  }, [product])

  useEffect(() => {
    if (mounted.current && isUrlStoreEncrypt && !product) history.push('/produto/todos')
  }, [product])

  return (
    <Grid className={classes.wrapper}>
      {isMobile && !loading ? (
        <Grid onClick={() => setOpenDrawer(true)} className={classes.wrapperAutocomplete} />
      ) : null}

      <Autocomplete
        autoSelect
        blurOnSelect
        autoComplete
        includeInputInList
        disableClearable
        classes={{
          paper: classes.paper,
          listbox: classes.listbox,
          groupLabel: classes.groupLabel,
          inputRoot: classes.inputRoot,
          input: classes.input,
          endAdornment: classes.endAdornment,
          noOptions: classes.noOptions
        }}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        loading={loading}
        disabled={loading}
        defaultValue={product}
        getOptionSelected={(option, value) => option.gtin === value.gtin}
        getOptionLabel={(option) => option.name}
        filterOptions={(options, state) => options}
        options={[defaultProduct, ...options]}
        value={selected}
        onChange={handleSelect}
        noOptionsText="Sem opções"
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              fullWidth
              placeholder={config.listingPlaceholderStore || 'Buscar qualquer produto'}
              color="primary"
              variant="outlined"
              onChange={(event) => setInputValue(event.target.value)}
              InputProps={{
                ...params.InputProps,
                readOnly: true
              }}
            />
          )
        }}
        popupIcon={<ExpandMoreIcon color={loading ? 'inherit' : 'primary'} fontSize="large" />}
        renderOption={(option) => {
          return (
            <Grid container alignItems="center" style={{ gap: 24 }}>
              <Grid item xs>
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  {option.name}
                </Typography>
              </Grid>
            </Grid>
          )
        }}
        PopperComponent={CustomPopper}
      />

      <DrawerProductByStore
        open={openDrawer}
        setOpen={setOpenDrawer}
        products={products}
        isMobile={isMobile}
      />
    </Grid>
  )
}
